import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { useAuth } from '../hooks/useAuth.js'

/**
 * Load a whole settings object, edit it locally, save it whole.
 *
 * Edits live in `draft` until saved, so a background refetch cannot overwrite
 * what somebody is typing — the failure that makes a settings form feel
 * possessed. `dirty` drives the Save button and the unsaved-changes warning.
 *
 * Whole-object saves rather than per-field patches, because that is what the
 * endpoints take: their fixed shape is the security boundary, and a partial PUT
 * per screen would let two tabs open at once overwrite each other's half.
 */
export function useSettingsResource<T extends object>(endpoint: string, key: string) {
  const { shop } = useAuth()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<T | null>(null)
  const [saved, setSaved] = useState(false)

  const query = useQuery({
    queryKey: [key, shop?.id],
    queryFn: () => api<T>(endpoint),
    enabled: Boolean(shop?.id),
  })

  const save = useMutation({
    mutationFn: (body: T) => api<T>(endpoint, { method: 'PUT', body }),
    onSuccess: (next) => {
      setDraft(next)
      setSaved(true)
      setTimeout(() => setSaved(false), 4000)
      void queryClient.invalidateQueries({ queryKey: [key, shop?.id] })
    },
  })

  const current = draft ?? query.data ?? null
  const dirty = draft !== null && query.data !== undefined && JSON.stringify(draft) !== JSON.stringify(query.data)

  const set = useCallback(
    <K extends keyof T>(field: K, value: T[K]) => {
      setDraft((previous) => {
        const base = previous ?? query.data
        return base ? { ...base, [field]: value } : null
      })
    },
    [query.data],
  )

  return {
    shop,
    settings: current,
    isPending: query.isPending,
    error: query.error,
    dirty,
    saved,
    saving: save.isPending,
    saveError: save.error,
    set,
    save: () => current && save.mutate(current),
  }
}
